import { formatDate, formatTime, describeBeaconHealth } from '@smarttransit/common'

export const EVENT_PAGE_SIZE = 10

export const LIST_HEADERS = [
  { text: 'Tag', value: 'id' },
  { text: 'Protocol', value: 'protocol', sortable: false },
  { text: 'Owner', value: 'ownerPhoneNumber', sortable: false },
  { text: 'Status', value: 'healthStatus' },
  { text: 'Battery', value: 'batteryPct' },
  { text: 'Last barcode', value: 'lastBarcodeSeenAt' },
  { text: 'Unheard rides', value: 'unheardRideCount' },
  { text: 'Replaced', value: 'batteryReplacedAt' }
]

export const EVENT_HEADERS = [
  { text: 'When', value: 'sortWhen', width: '168px' },
  { text: 'Event', value: 'title' },
  { text: 'Source', value: 'source', width: '96px' },
  { text: 'By', value: 'stUserId', width: '72px' },
  { text: 'Note', value: 'note' }
]

const STATUS_OPTIONS = [
  { text: 'Not detected', value: 'not_detected' },
  { text: 'Low', value: 'low' },
  { text: 'OK', value: 'ok' },
  { text: 'Unknown', value: 'unknown' }
]

const REASON_LABELS = {
  unheard_rides: 'Not heard on recent rides',
  confirmed_low: 'Confirmed low battery',
  radio_reading: 'Radio reading'
}

function toDate (value) {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  if (isNaN(date.getTime())) return null
  return date
}

export function formatWhen (value) {
  const date = toDate(value)
  if (!date) return ''
  return formatDate(date) + ' ' + formatTime(date)
}

export function relativeAge (value, now) {
  const date = toDate(value)
  if (!date) return ''
  const clock = toDate(now) || new Date()
  const elapsed = clock.getTime() - date.getTime()
  if (elapsed < 0) return ''
  if (elapsed < 60000) return 'just now'

  const minutes = Math.floor(elapsed / 60000)
  if (minutes < 60) return minutes === 1 ? '1 minute ago' : minutes + ' minutes ago'

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return hours === 1 ? '1 hour ago' : hours + ' hours ago'

  const days = Math.floor(hours / 24)
  return days === 1 ? '1 day ago' : days + ' days ago'
}

export function withAge (value, now) {
  const when = formatWhen(value)
  if (!when) return '—'
  const age = relativeAge(value, now)
  if (!age) return when
  return when + ' · ' + age
}

export function statusText (status) {
  if (!status) return ''
  const match = STATUS_OPTIONS.find((option) => option.value === status)
  return match ? match.text : status
}

export function reasonText (reason) {
  if (!reason) return ''
  return REASON_LABELS[reason] || reason
}

export function batteryText (item) {
  if (!item) return ''
  const parts = []
  if (item.batteryPct !== null && item.batteryPct !== undefined) parts.push(item.batteryPct + '%')
  if (item.batteryMv !== null && item.batteryMv !== undefined) parts.push(item.batteryMv + ' mV')
  return parts.join(' / ')
}

function displayValue (value) {
  if (value === 0) return '0'
  if (value) return String(value)
  return '—'
}

function describeStatus (beacon) {
  if (!beacon) return null
  const metadata = {
    battery_low: beacon.batteryLow,
    beaconHealth: {
      status: beacon.healthStatus,
      reason: beacon.healthReason,
      batteryReadAt: beacon.batteryReadAt,
      lastRadioSeenAt: beacon.lastRadioSeenAt,
      lastBarcodeSeenAt: beacon.lastBarcodeSeenAt,
      unheardRideCount: beacon.unheardRideCount
    }
  }
  if (typeof beacon.batteryPct === 'number') metadata.battery_pct = beacon.batteryPct
  return describeBeaconHealth(metadata)
}

export function presentBeacon (item) {
  if (!item) return null
  const described = describeStatus(item)
  return Object.assign({}, item, {
    sourceLabel: item.protocol || item.manufacturer || '',
    statusLabel: described ? described.label : statusText(item.healthStatus),
    statusIcon: described ? described.icon : '',
    statusColor: described ? described.color : '',
    batteryLabel: batteryText(item),
    barcodeLabel: formatWhen(item.lastBarcodeSeenAt),
    replacedLabel: formatWhen(item.batteryReplacedAt)
  })
}

export function identityRows (beacon) {
  if (!beacon) return []
  const rows = [
    { label: 'Protocol', value: displayValue(beacon.protocol) },
    { label: 'Manufacturer', value: displayValue(beacon.manufacturer) }
  ]
  if (beacon.identifierType) rows.push({ label: 'Type', value: String(beacon.identifierType) })
  rows.push({
    label: 'Owner',
    value: beacon.ownerPhoneNumber || (beacon.ownerPassengerProfileId ? 'Passenger' : '—'),
    passengerId: beacon.ownerPassengerProfileId || ''
  })
  rows.push(
    { label: 'Created', value: formatWhen(beacon.dateCreated) || '—' },
    { label: 'Updated', value: formatWhen(beacon.dateUpdated) || '—' }
  )
  return rows
}

export function healthRows (beacon, now) {
  if (!beacon) return []
  const described = describeStatus(beacon)
  const rows = [{
    label: 'Status',
    value: described ? described.label : (statusText(beacon.healthStatus) || '—'),
    icon: described ? described.icon : '',
    color: described ? described.color : ''
  }]
  const reason = reasonText(beacon.healthReason)
  if (reason) rows.push({ label: 'Reason', value: reason })
  rows.push(
    { label: 'Battery', value: batteryText(beacon) || '—' },
    { label: 'Reading', value: withAge(beacon.batteryReadAt, now) },
    { label: 'Last radio', value: withAge(beacon.lastRadioSeenAt, now) },
    { label: 'Last barcode', value: withAge(beacon.lastBarcodeSeenAt, now) },
    { label: 'Unheard rides', value: displayValue(beacon.unheardRideCount) },
    { label: 'Replaced', value: withAge(beacon.batteryReplacedAt, now) }
  )
  if (beacon.unheardSince) rows.push({ label: 'Unheard since', value: withAge(beacon.unheardSince, now) })
  if (beacon.batteryLowSeenAt) rows.push({ label: 'Low since', value: withAge(beacon.batteryLowSeenAt, now) })
  return rows
}

function eventTitle (event) {
  if (event.eventType === 'note') return 'Note'
  if (event.eventType === 'battery_replaced') return 'Battery replaced'
  if (event.eventType === 'status_changed') {
    const metadata = event.metadata || {}
    const change = 'Status ' + (statusText(metadata.from) || '?') + ' to ' + (statusText(metadata.to) || '?')
    const reason = reasonText(metadata.reason)
    return reason ? change + ' · ' + reason : change
  }
  if (event.eventType === 'registered') {
    return event.metadata && event.metadata.recovered ? 'Registered again' : 'Registered'
  }
  if (event.eventType === 'unregistered') return 'Unregistered'
  return event.eventType || ''
}

function actorLabel (event) {
  if (!event || event.stUserId === null || event.stUserId === undefined || event.stUserId === '') return ''
  return String(event.stUserId)
}

export function formatEvent (event) {
  const source = event || {}
  const date = toDate(source.dateCreated)
  return Object.assign({}, source, {
    whenLabel: formatWhen(source.dateCreated) || '—',
    sortWhen: date ? date.getTime() : 0,
    title: eventTitle(source),
    source: source.source || '',
    actorLabel: actorLabel(source),
    note: source.note || ''
  })
}

export function replacementInstant (value, now) {
  if (value === null || value === undefined || String(value).trim() === '') return null
  const date = value instanceof Date ? value : new Date(value)
  if (isNaN(date.getTime())) return { error: 'Enter a valid replacement date.' }
  const clock = toDate(now) || new Date()
  if (date.getTime() > clock.getTime()) return { error: 'Replacement date cannot be in the future.' }
  return { at: date.toISOString() }
}

function pad2 (value) {
  return value < 10 ? '0' + value : String(value)
}

export function dateTimeLocalMax (now) {
  const date = toDate(now) || new Date()
  return date.getFullYear() + '-' + pad2(date.getMonth() + 1) + '-' + pad2(date.getDate()) + 'T' + pad2(date.getHours()) + ':' + pad2(date.getMinutes())
}

export function shouldPageEvents (events) {
  const count = Array.isArray(events) ? events.length : 0
  return count > EVENT_PAGE_SIZE
}

export function mergeBeaconDetail (listItem, historyBeacon) {
  if (!historyBeacon) return listItem || null
  const merged = Object.assign({}, listItem || {}, historyBeacon)
  if (listItem && !historyBeacon.ownerPassengerProfileId && listItem.ownerPassengerProfileId) {
    merged.ownerPassengerProfileId = listItem.ownerPassengerProfileId
    merged.ownerPhoneNumber = listItem.ownerPhoneNumber || null
    merged.ownerAlternateIdentifierId = listItem.ownerAlternateIdentifierId || null
  }
  return merged
}

export function shouldKeepEvents (current, item, expandEvents) {
  if (!expandEvents || !current || !item) return false
  return current.id === item.id
}

export function historyParts (history) {
  if (!history) return { beacon: null, events: [] }
  return {
    beacon: history.beacon || null,
    events: Array.isArray(history.events) ? history.events : []
  }
}
